import React, { useMemo, useState } from "react";
import { SectionTitle, InputCss } from "../../_parts.js";
import BorrowerSteppedForm from "../shared/BorrowerSteppedForm.jsx";
import FormField, { FormFieldRow } from "../shared/FormField.jsx";
import FormUploadsPanel from "../shared/FormUploadsPanel.jsx";
import FormReviewPanel from "../shared/FormReviewPanel.jsx";
import CreditHistoryField from "../shared/CreditHistoryField.jsx";
import { getDraftStorageKey, loadFormDraft } from "../shared/useBorrowerFormDraft.js";
import { useBtlUploads } from "../shared/useBtlUploads.js";
import { buildUploadLabelMap, digitsOnly, isValidEmail } from "../shared/formValidation.js";
import PhoneField from "../../../../components/ui/PhoneField.jsx";
import {
  arePhonesValid,
  isValidPhone,
  PHONE_ERROR_GENERIC,
  PHONE_ERROR_MOBILE,
} from "../../../../utils/validatePhone.js";
import { IF_FORM_WIZARD_STEPS, IF_UPLOAD_BUCKETS } from "../shared/borrowerUploadConfig.js";

const uid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const MAX_APPLICANTS = 5;

export default function IfLimitedForm({
  onBack,
  onNext,
  initialPayload,
  productId,
  isSubmitting = false,
}) {
  const savedDraft = loadFormDraft(getDraftStorageKey("if-limited", productId || initialPayload?.productId));
  const initial = savedDraft?.if?.limited || initialPayload?.if?.limited || {};

  const [company, setCompany] = useState({
    name: initial.company?.name || "",
    regNo: initial.company?.regNo || "",
    phone: initial.company?.phone || "",
    email: initial.company?.email || "",
  });
  const [credit, setCredit] = useState(initial.credit || "");
  const [applicants, setApplicants] = useState(
    initial.applicants?.length
      ? initial.applicants
      : [{ id: uid(), firstName: "", surname: "", phone: "", email: "" }]
  );

  const { uploads, uploadingFiles, handleUpload, handleRemove } = useBtlUploads(initial.uploads || {});
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});
  const primary = applicants[0] || {};
  const touch = (key) => setTouched((t) => ({ ...t, [key]: true }));

  const updateApplicant = (id, key, val) =>
    setApplicants((list) => list.map((a) => (a.id === id ? { ...a, [key]: val } : a)));

  const payloadObj = useMemo(
    () => ({
      ...(initialPayload || {}),
      if: {
        ...(initialPayload?.if || {}),
        borrowerType: "Ltd Co.",
        limited: { company, applicants, uploads, credit },
      },
      formData: {
        ...(initialPayload?.formData || {}),
        credit,
        credit_history: credit,
      },
    }),
    [initialPayload, company, applicants, uploads, credit]
  );

  const validateDetails = () => {
    const err = {};
    if (!company.name.trim()) err.company_name = "Company Name is required.";
    if (!company.regNo.trim()) err.company_regNo = "Company Registration Number is required.";
    if (!isValidPhone(company.phone, { mobileOnly: false })) err.company_phone = PHONE_ERROR_GENERIC;
    if (!company.email.trim()) err.company_email = "Company Email is required.";
    else if (!isValidEmail(company.email)) err.company_email = "Enter a valid email address.";
    if (!primary.firstName?.trim()) err.primary_firstName = "Primary applicant First Name is required.";
    if (!primary.surname?.trim()) err.primary_surname = "Primary applicant Surname is required.";
    if (!isValidPhone(primary.phone)) err.primary_phone = PHONE_ERROR_MOBILE;
    if (!primary.email?.trim()) err.primary_email = "Primary applicant Email is required.";
    else if (!isValidEmail(primary.email)) err.primary_email = "Enter a valid email address.";
    if (!credit) err.credit = "Please select credit history.";
    setFieldErrors(err);
    setTouched(Object.fromEntries(Object.keys(err).map((k) => [k, true])));
    return !Object.keys(err).length;
  };

  const stepErrors = useMemo(
    () => ({
      details: Object.keys(fieldErrors).length > 0,
      documents: false,
      review: false,
    }),
    [fieldErrors]
  );

  const validateStep = async (stepIndex, stepId) => {
    if (stepId === "details" || stepIndex === 0) return validateDetails();
    return true;
  };

  const finish = (extras = {}) => {
    onNext?.({
      type: "limited",
      company,
      applicants,
      credit,
      uploads,
      payloadObj,
      submitFromReview: true,
      agree1: extras.agree1,
      agree2: extras.agree2,
    });
  };

  const uploadLabels = useMemo(() => buildUploadLabelMap(IF_UPLOAD_BUCKETS), []);

  return (
    <>
      <BorrowerSteppedForm
        title="INVOICE FINANCE — LTD / LLP"
        subtitle="Please complete all required fields and upload necessary documents"
        steps={IF_FORM_WIZARD_STEPS}
        draftKey={getDraftStorageKey("if-limited", productId || initialPayload?.productId)}
        draftSnapshot={payloadObj}
        stepErrors={stepErrors}
        validateStep={validateStep}
        onOuterBack={onBack}
        isSubmitting={isSubmitting}
        detailsContinueDisabled={!arePhonesValid([
          { value: company.phone, mobileOnly: false },
          ...applicants.map((a, i) => ({ value: a.phone, required: i === 0 })),
        ])}
        renderDetails={() => (
          <div className="space-y-5">
            <div>
              <SectionTitle>Company</SectionTitle>
              <FormFieldRow>
                <FormField id="if-co-name" label="Company Name" required error={fieldErrors.company_name} touched={touched.company_name}>
                  <input id="if-co-name" className="input w-full" value={company.name} onChange={(e) => setCompany((c) => ({ ...c, name: e.target.value }))} onBlur={() => touch("company_name")} />
                </FormField>
                <FormField id="if-co-reg" label="Company Registration Number" required error={fieldErrors.company_regNo} touched={touched.company_regNo}>
                  <input id="if-co-reg" className="input w-full" value={company.regNo} onChange={(e) => setCompany((c) => ({ ...c, regNo: digitsOnly(e.target.value) }))} onBlur={() => touch("company_regNo")} />
                </FormField>
                <FormField id="if-co-phone" label="Company Telephone" required error={fieldErrors.company_phone} touched={touched.company_phone}>
                  <PhoneField
                    id="if-co-phone"
                    value={company.phone}
                    mobileOnly={false}
                    onChange={(next) => setCompany((c) => ({ ...c, phone: next }))}
                    onBlur={() => touch("company_phone")}
                  />
                </FormField>
                <FormField id="if-co-email" label="Company Email" required error={fieldErrors.company_email} touched={touched.company_email}>
                  <input id="if-co-email" type="email" className="input w-full" value={company.email} onChange={(e) => setCompany((c) => ({ ...c, email: e.target.value }))} onBlur={() => touch("company_email")} />
                </FormField>
              </FormFieldRow>
            </div>
            <CreditHistoryField id="if-limited-credit" value={credit} error={fieldErrors.credit} touched={touched.credit} onChange={setCredit} />
            <div>
              <SectionTitle>Applicants</SectionTitle>
              {applicants.map((a, idx) => (
                <div key={a.id} className="rounded-xl border border-slate-200 p-3 mb-3">
                  <div className="mb-2 flex justify-between items-center">
                    <p className="text-sm font-bold text-slate-900">Applicant {idx + 1}</p>
                    {applicants.length > 1 ? (
                      <button type="button" className="text-xs text-red-500" onClick={() => setApplicants((list) => list.filter((x) => x.id !== a.id))}>Remove</button>
                    ) : null}
                  </div>
                  <FormFieldRow>
                    <FormField id={`if-app-${a.id}-fn`} label="First Name" required={idx === 0} error={idx === 0 ? fieldErrors.primary_firstName : undefined} touched={idx === 0 ? touched.primary_firstName : false}>
                      <input className="input w-full" value={a.firstName} onChange={(e) => updateApplicant(a.id, "firstName", e.target.value)} onBlur={() => idx === 0 && touch("primary_firstName")} />
                    </FormField>
                    <FormField id={`if-app-${a.id}-sn`} label="Surname" required={idx === 0} error={idx === 0 ? fieldErrors.primary_surname : undefined} touched={idx === 0 ? touched.primary_surname : false}>
                      <input className="input w-full" value={a.surname} onChange={(e) => updateApplicant(a.id, "surname", e.target.value)} onBlur={() => idx === 0 && touch("primary_surname")} />
                    </FormField>
                    <FormField id={`if-app-${a.id}-ph`} label="Phone Number" required={idx === 0}>
                      <PhoneField
                        value={a.phone}
                        required={idx === 0}
                        onChange={(next) => updateApplicant(a.id, "phone", next)}
                      />
                    </FormField>
                    <FormField id={`if-app-${a.id}-em`} label="Email" required={idx === 0} error={idx === 0 ? fieldErrors.primary_email : undefined} touched={idx === 0 ? touched.primary_email : false}>
                      <input type="email" className="input w-full" value={a.email} onChange={(e) => updateApplicant(a.id, "email", e.target.value)} onBlur={() => idx === 0 && touch("primary_email")} />
                    </FormField>
                  </FormFieldRow>
                </div>
              ))}
              {applicants.length < MAX_APPLICANTS ? (
                <button type="button" className="text-sm font-semibold text-sky-600" onClick={() => setApplicants((a) => [...a, { id: uid(), firstName: "", surname: "", phone: "", email: "" }])}>
                  + Add applicant
                </button>
              ) : null}
            </div>
          </div>
        )}
        renderDocuments={() => (
          <FormUploadsPanel buckets={IF_UPLOAD_BUCKETS} uploads={uploads} uploadingFiles={uploadingFiles} onUpload={handleUpload} onRemove={handleRemove} />
        )}
        renderReview={({ goToStep, onFooterStateChange }) => (
          <FormReviewPanel
            hideSubmitButton
            documentsStepIndex={1}
            onFooterStateChange={onFooterStateChange}
            sections={[
              {
                stepId: "details",
                stepIndex: 0,
                title: "Your details",
                rows: [
                  { label: "Company", value: company.name },
                  { label: "Reg. No.", value: company.regNo },
                  { label: "Company phone", value: company.phone },
                  { label: "Company email", value: company.email },
                  { label: "Credit History", value: credit },
                ],
                content: applicants.map((a, i) => (
                  <p key={a.id} className="text-sm">
                    <span className="text-slate-500">Applicant {i + 1}: </span>
                    {a.firstName} {a.surname} · {a.phone} · {a.email}
                  </p>
                )),
              },
            ]}
            uploads={uploads}
            uploadLabels={uploadLabels}
            onEditStep={goToStep}
            isSubmitting={isSubmitting}
            onSubmit={({ agree1, agree2 }) => finish({ agree1, agree2 })}
          />
        )}
      />
      <InputCss />
    </>
  );
}
